import { Layout } from './layout';

import { LayerViewModel } from '../view-models/chart-viewmodel';
import { ChannelType } from '../models/ichannel';

import { ECG_CONSTANTS } from '../utils/ecg-constants';
import { TimeRange, TraceViewModel } from '../view-models/trace-viewmodel';
import { ArrangementType, Format, PaperRate, TimeAlignment } from '../models/emuns';

class ChangeDetection<T> {
    // data members

    private _isFirstUse = true;
    private _value: T;

    // constructor

    constructor(defaultValue: T) {
        this._value = defaultValue;
    }

    // public functions

    isChanged(value: T): boolean {
        if (this._isFirstUse) {
            this._value = value;
            this._isFirstUse = false;
            return true;
        } else if (this._value != value) {
            this._value = value;
            return true;
        }

        return false;
    }
}

export interface ILayerComposer {
    update(layers: Array<LayerViewModel>, force?: boolean): void;
    updateSingle(layer: LayerViewModel, force?: boolean): void;
    isChanged(): boolean;
}

export class ChangeDetectionComposer<T> implements ILayerComposer {
    // data members

    protected layout: Layout;
    protected changeDetection: ChangeDetection<T>;

    // constructor

    constructor(layout: Layout, defaultValue: T) {
        this.layout = layout;
        this.changeDetection = new ChangeDetection<T>(defaultValue);
    }

    // public functions

    update(layers: Array<LayerViewModel>, force = false) {
        if (!force && !this.isChanged()) return;

        this.onUpdateAll(layers);
    }

    updateSingle(layer: LayerViewModel, force = false) {
        if (!force && !this.isChanged()) return;

        this.onUpdate(layer);
    }

    public isChanged() {
        return false;
    }

    // protected functions

    protected onUpdateAll(layers: Array<LayerViewModel>) {
        layers.forEach((layer) => this.onUpdate(layer));
    }

    protected onUpdate(_: LayerViewModel) {}
}

export class RelocateComposer<T> extends ChangeDetectionComposer<T> {
    // constructor

    constructor(layout: Layout, defaultValue: T) {
        super(layout, defaultValue);
    }

    // pubblic functions

    public isChanged() {
        return true;
    }

    // protected functions

    protected onUpdate(layer: LayerViewModel) {
        const dictionary: Map<string, number> = new Map<string, number>();
        const leadHeight = this.layout.context?.leadHeight ?? 0;
        const size = {
            width: this.layout.getSize().width,
            height: this.layout.getSize().height + layer.referenceTraces.length,
        };

        layer.visibleTraces.forEach((trace, index) => {
            const key = trace.step?.title ?? '';
            const dnc = size.height / 2 - (this.layout.getPosition(index).y + leadHeight / 2);
            const value = dnc * this.layout.ecgState.chartState.traceSpacing;
            dictionary.set(key, value);
        });
        layer.visibleReferenceTraces.forEach((trace, index) => {
            const key = trace.step?.title ?? '';
            const dnc = size.height / 2 - (this.layout.rows * leadHeight + index * leadHeight + leadHeight / 2);
            const value = dnc * this.layout.ecgState.chartState.traceSpacing;
            dictionary.set(key, value);
        });

        layer.visibleTraces.forEach((trace, index) => {
            const key = trace.step?.title ?? '';
            const value = dictionary.get(key) ?? 0;
            const position = this.layout.getPosition(index);
            position.y = size.height / 2 - value - leadHeight / 2;

            trace.translate =
                'translate(' + (position.x + 5) + ', ' + Math.ceil((position.y / 5000) * 5000).toString() + ')';
        });
        layer.visibleReferenceTraces.forEach((trace, index) => {
            const key = trace.step?.title ?? '';
            const position = {
                x: ECG_CONSTANTS.LEAD_SPACE_WIDTH,
                y: this.layout.rows * leadHeight + index * leadHeight,
            };
            const value = dictionary.get(key) ?? 0;
            position.y = size.height / 2 - value - leadHeight / 2;

            trace.translate =
                'translate(' + (position.x + 5) + ', ' + Math.ceil((position.y / 5000) * 5000).toString() + ')';
        });
    }
}

export class TraceSpacingComposer extends RelocateComposer<number> {
    // constructor

    constructor(layout: Layout) {
        super(layout, 0);
    }

    public isChanged() {
        return true;
    }

    // protected functions

    onUpdate(layer: LayerViewModel) {
        super.onUpdate(layer);
    }
}

export class CabreraComposer extends RelocateComposer<Format> {
    // constructor

    constructor(layout: Layout) {
        super(layout, Format.standard);
    }

    public isChanged() {
        return this.changeDetection.isChanged(this.layout.ecgState.chartState.format);
    }

    // protected functions

    protected onUpdate(layer: LayerViewModel) {
        const traceOrder =
            this.layout.ecgState.chartState.format != Format.cabrera
                ? ECG_CONSTANTS.CHANNELS_STANDARD_ORDER
                : ECG_CONSTANTS.CHANNELS_CABRERA_ORDER;

        let swapper = (sourceIndex: number, targetIndex: number) => {
            let tmp = layer.traces[targetIndex];
            layer.traces[targetIndex] = layer.traces[sourceIndex];
            layer.traces[sourceIndex] = tmp;
        };

        if (this.layout.ecgState.chartState.arrangement != ArrangementType.SixXOnePrecordial) {
            traceOrder.forEach((name, index) => {
                const traceIndex = layer.traces.findIndex((trace) => trace.channel?.type === name);
                if (traceIndex !== -1) {
                    swapper(index, traceIndex);
                }
            });
        }

        this.updateAVR(layer);

        super.onUpdate(layer);
    }

    // private functions

    private updateAVR(layer: LayerViewModel) {
        const avr = layer.traces.find((avrTrace) => avrTrace.channel?.type === ChannelType.LEAD_AVR);
        if (!avr) {
            return;
        }

        if (this.layout.ecgState.chartState.format == Format.standard && avr.step?.title.startsWith('-')) {
            avr.channel?.samples.forEach((sample) => (sample.value = -sample.value));
            if (!!avr.step) avr.step.title = avr.step.title.replace('-', '');
        } else if (this.layout.ecgState.chartState.format == Format.cabrera && !avr.step?.title.startsWith('-')) {
            avr.channel?.samples.forEach((sample) => (sample.value = -sample.value));
            if (!!avr.step) avr.step.title = '-' + avr.step.title;
        }

        avr.updateTrace();
    }
}

export class TimeAlignmentComposer extends ChangeDetectionComposer<TimeAlignment> {
    // constructor

    constructor(layout: Layout) {
        super(layout, TimeAlignment.Simultaneous);
    }

    // public functions

    public isChanged() {
        return this.changeDetection.isChanged(this.layout.ecgState.chartState.timeAlignment);
    }

    // protected functions

    protected onUpdate(layer: LayerViewModel) {
        if (this.layout.ecgState.chartState.arrangement === ArrangementType.Medians) {
            layer.visibleTraces.forEach((trace) => {
                const samplesRange = { start: 0, end: trace.channel?.samples.length ?? 0 };
                this.updateTraceTimeAlignment(trace, samplesRange);
            });

            return;
        }

        switch (this.layout.ecgState.chartState.timeAlignment) {
            case TimeAlignment.Sequential:
                for (let column = 0; column < this.layout.columns; column++) {
                    for (let row = 0; row < this.layout.rows; row++) {
                        const trace = layer.visibleTraces[column * this.layout.rows + row];
                        if (!trace) {
                            continue;
                        }
                        const numberOfSamples = trace.channel?.samples.length ?? 0;
                        let samplesRange = {
                            start: (column * numberOfSamples) / this.layout.columns,
                            end:
                                (column * numberOfSamples) / this.layout.columns +
                                numberOfSamples / this.layout.columns,
                        };
                        this.updateTraceTimeAlignment(trace, samplesRange);
                    }
                }

                layer.referenceTraces.forEach((trace) => {
                    const numberOfSamples = trace.channel?.samples.length ?? 0;
                    this.updateTraceTimeAlignment(trace, { start: 0, end: numberOfSamples });
                });
                // let referenceTrace = layer.traces[layer.traces.length - 1];
                // if (referenceTrace) {
                //     this.updateTraceTimeAlignment(referenceTrace, { start: 0, end: referenceTrace.channel.samples.length });
                // }

                break;
            default:
                layer.visibleTraces.forEach((trace) => {
                    const numberOfSamples = trace.channel?.samples.length ?? 0;
                    const samplesRange = { start: 0, end: numberOfSamples / this.layout.columns };
                    this.updateTraceTimeAlignment(trace, samplesRange);
                });
                break;
        }
    }

    private updateTraceTimeAlignment(trace: TraceViewModel, range: TimeRange) {
        trace.updateTraceTimeAlignment(range);
    }
}

export class PaperRateComposer extends RelocateComposer<PaperRate> {
    // data members

    timeLineComposer: ILayerComposer;

    // constructor

    constructor(layout: Layout) {
        super(layout, PaperRate.mm25);

        this.timeLineComposer = new TimeAlignmentComposer(layout);
    }

    // public functions

    public isChanged() {
        return this.changeDetection.isChanged(this.layout.ecgState.chartState.paperRate);
    }

    // protected functions

    protected onUpdateAll(layers: Array<LayerViewModel>) {
        this.timeLineComposer.update(layers, true);

        super.onUpdateAll(layers);
    }

    protected onUpdate(layer: LayerViewModel) {
        super.onUpdate(layer);
    }
}

export class ArrangementComposer extends RelocateComposer<ArrangementType> {
    // data members

    private paperRateComposer: ILayerComposer;
    private timeAlignmentComposer: ILayerComposer;
    private cabreraComposer: ILayerComposer;

    // constructor

    constructor(layout: Layout) {
        super(layout, ArrangementType.SixXTwo);

        this.paperRateComposer = new PaperRateComposer(layout);
        this.timeAlignmentComposer = new TimeAlignmentComposer(layout);
        this.cabreraComposer = new CabreraComposer(layout);
    }

    // public functions

    public isChanged() {
        return this.changeDetection.isChanged(this.layout.ecgState.chartState.arrangement);
    }

    // protected functions

    protected onUpdateAll(layers: Array<LayerViewModel>) {
        super.onUpdateAll(layers);

        this.paperRateComposer.update(layers, true);
        this.timeAlignmentComposer.update(layers, true);
        this.cabreraComposer.update(layers, true);
    }

    protected onUpdate(layer: LayerViewModel) {
        if (layer.traces.length === 0) {
            return;
        }

        layer.traces.forEach((trace) => {
            trace.visible = true;
            //trace.index = index;
        });

        layer.referenceTraces.forEach((trace) => (trace.visible = false));

        switch (this.layout.ecgState.chartState.arrangement) {
            case ArrangementType.SixXTwo:
                this.layout.rows = 6;
                this.layout.columns = 2;
                break;
            case ArrangementType.ThreeXFour:
                if (layer.traces.length === 15) {
                    for (let i = 12; i < 15; i++) {
                        layer.traces[i].visible = false;
                    }
                }
                this.layout.rows = 3;
                this.layout.columns = 4;
                layer.referenceTraces.forEach((trace) => (trace.visible = true));
                break;
            case ArrangementType.TwelveXOne:
                this.layout.rows = 12;
                this.layout.columns = 1;

                break;
            case ArrangementType.SixXOnePrecordial:
                this.layout.rows = 6;
                this.layout.columns = 1;
                layer.traces.filter((trace, index) => index < 6).forEach((trace) => (trace.visible = false));
                break;

            case ArrangementType.SixXOnePeripheral:
                this.layout.rows = 6;
                this.layout.columns = 1;
                layer.traces.filter((trace, index) => index >= 6).forEach((trace) => (trace.visible = false));
                break;

            case ArrangementType.Medians:
                this.layout.rows = 3;
                this.layout.columns = 4;
                break;

            case ArrangementType.XXOne:
                this.layout.rows = layer.traces.length;
                this.layout.columns = 1;
                break;

            case ArrangementType.XXTwo:
                this.layout.rows = Math.ceil(layer.traces.length / 2);
                this.layout.columns = 2;
                break;
        }

        this.layout.updateLeadWidth();

        super.onUpdate(layer);
    }
}

export class ChartSpacingComposer extends ChangeDetectionComposer<number> {
    // constructor

    constructor(layout: Layout) {
        super(layout, 0);
    }

    public isChanged() {
        return true;
    }

    // protected functions

    protected onUpdateAll(layers: Array<LayerViewModel>) {
        layers.sort((layerA, layerB) => {
            if (layerA.active) return -1;
            if (layerB.active) return 1;
            return 0;
        });

        layers.forEach((layer, index) => {
            layer.index = index;
        });

        const dictionaryY: Record<string, number> = {};
        const dictionaryX: Record<string, number> = {};
        const leadHeight = this.layout.context?.leadHeight ?? 0;
        const leadWidth = this.layout.context?.leadWidth ?? 0;
        this.layout.context?.layers.forEach((layer) => {
            const dncY =
                (this.layout.getLayerPosition(0).y + leadHeight) / 2 -
                (this.layout.getLayerPosition(layer.index).y + leadHeight / 2);
            const dncX =
                (this.layout.getLayerPosition(0).x + leadWidth) / 2 -
                (this.layout.getLayerPosition(layer.index).y + leadWidth / 2);
            dictionaryY[layer.title] = dncY * this.layout.ecgState.multiViewerState.studySpacing;
            dictionaryX[layer.title] = dncX * this.layout.ecgState.multiViewerState.horizontalStudySpacing;
        });

        this.layout.context?.layers.forEach((layer) => {
            const position = this.layout.getLayerPosition(layer.index);
            position.y =
                (this.layout.getLayerPosition(0).y + leadHeight) / 2 - dictionaryY[layer.title] - leadHeight / 2;
            position.x = (this.layout.getLayerPosition(0).x + leadWidth) / 2 - dictionaryX[layer.title] - leadWidth / 2;

            layer.translate =
                'translate(' +
                (Math.ceil(position.x * 5000) / 5000).toString() +
                ', ' +
                (Math.ceil(position.y * 5000) / 5000).toString() +
                ')';
        });
    }
}
